/**
 * Filterable combobox variant of dtpSelect.js (Category/Reason fields).
 * Trigger is an <input> that filters options as the user types.
 *
 * createDtpComboboxSelect: single-select, keeps picked label in the input.
 * createDtpComboboxMultiselect: multi-select, clears input and renders chips externally.
 */

import {
  optionDomId,
  createComboboxTrigger,
  createListbox,
  setActiveIndex as setActiveIndexShared,
  createOutsideClickHandler,
  setTriggerDisabled,
} from './dtpSelectShared';
import { createIcon } from '../../../icons';

let instanceCounter = 0;

function normalize(text) {
  return (text || '').toLowerCase().trim();
}

function createControl(trigger, chevron) {
  const control = document.createElement('div');
  control.className = 'dtp-select__control';
  control.appendChild(trigger);
  control.appendChild(chevron);
  return control;
}

function createOptionElement(namespace, option, checkboxStyle) {
  const optionEl = document.createElement('div');
  optionEl.className = checkboxStyle
    ? 'dtp-select__option dtp-select__option--checkbox'
    : 'dtp-select__option';
  optionEl.id = optionDomId(namespace, option.id);
  optionEl.setAttribute('role', 'option');
  optionEl.setAttribute('data-value', option.id);

  if (checkboxStyle) {
    const optionCheckbox = document.createElement('span');
    optionCheckbox.className = 'dtp-select__option-checkbox';
    optionCheckbox.setAttribute('aria-hidden', 'true');
    optionCheckbox.appendChild(createIcon('checkbox-check'));
    optionEl.appendChild(optionCheckbox);
  }

  const optionLabel = document.createElement('span');
  optionLabel.className = 'dtp-select__option-label';
  optionLabel.textContent = option.label;
  optionEl.appendChild(optionLabel);

  if (!checkboxStyle) {
    optionEl.appendChild(createIcon('check', 'dtp-select__option-check'));
  }

  return optionEl;
}

export function createDtpComboboxSelect({
  id,
  options,
  placeholder = 'Select',
  selectedId = null,
  labelId,
} = {}) {
  const namespace = id || `dtp-combobox-${++instanceCounter}`;

  const container = document.createElement('div');
  container.className = 'dtp-select dtp-select--combobox';

  // Hidden native <select> keeps field.inputs[0].value working.
  const nativeSelect = document.createElement('select');
  nativeSelect.className = 'dtp-select__native';
  nativeSelect.id = namespace;
  nativeSelect.tabIndex = -1;
  nativeSelect.setAttribute('aria-hidden', 'true');

  const placeholderOption = document.createElement('option');
  placeholderOption.value = '';
  placeholderOption.textContent = placeholder;
  nativeSelect.appendChild(placeholderOption);

  options.forEach((option) => {
    const optionEl = document.createElement('option');
    optionEl.value = option.id;
    optionEl.textContent = option.label;
    optionEl.setAttribute('data-valuecode', option.id);
    nativeSelect.appendChild(optionEl);
  });
  nativeSelect.value = selectedId || '';

  const trigger = createComboboxTrigger({ namespace, labelId, placeholder });
  const chevron = createIcon('chevron', 'dtp-select__chevron');
  const listbox = createListbox({ namespace, labelId });

  const optionElements = options.map((option) => {
    const optionEl = createOptionElement(namespace, option, false);
    listbox.appendChild(optionEl);
    return optionEl;
  });

  container.appendChild(createControl(trigger, chevron));
  container.appendChild(listbox);
  container.appendChild(nativeSelect);

  let activeIndex = -1;
  let isOpen = false;

  function visibleOptionElements() {
    return optionElements.filter((el) => !el.hidden);
  }

  function applyFilter(query) {
    const normalizedQuery = normalize(query);
    optionElements.forEach((optionEl, index) => {
      const option = options[index];
      optionEl.hidden =
        Boolean(normalizedQuery) && !normalize(option.label).includes(normalizedQuery);
    });
    listbox.classList.toggle('dtp-select__listbox--empty', !visibleOptionElements().length);
  }

  function updateSelectedState() {
    const currentValue = nativeSelect.value;
    optionElements.forEach((optionEl) => {
      const isSelected = optionEl.getAttribute('data-value') === currentValue;
      optionEl.setAttribute('aria-selected', String(isSelected));
      optionEl.classList.toggle('dtp-select__option--selected', isSelected);
    });
  }

  function syncInputFromSelection() {
    const selectedOption = options.find((option) => option.id === nativeSelect.value);
    trigger.value = selectedOption ? selectedOption.label : '';
  }

  function setActiveIndex(index, { scrollIntoView = true } = {}) {
    activeIndex = index;
    setActiveIndexShared({
      trigger,
      optionElements: visibleOptionElements(),
      index,
      scrollIntoView,
    });
  }

  function openListbox() {
    if (isOpen || trigger.disabled) {
      return;
    }
    isOpen = true;
    outsideClick.enable();
    container.classList.add('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'true');
    applyFilter('');
    const selectedIndex = visibleOptionElements().findIndex(
      (el) => el.getAttribute('data-value') === nativeSelect.value,
    );
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : -1);
  }

  function closeListbox({ restoreFocus = false } = {}) {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    outsideClick.disable();
    container.classList.remove('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'false');
    setActiveIndex(-1);

    if (trigger.value.trim() === '' && nativeSelect.value !== '') {
      nativeSelect.value = '';
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      syncInputFromSelection();
    }
    if (restoreFocus) {
      trigger.focus();
    }
  }

  function selectOption(optionId) {
    if (nativeSelect.value !== optionId) {
      nativeSelect.value = optionId;
      updateSelectedState();
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncInputFromSelection();
    closeListbox({ restoreFocus: true });
  }

  nativeSelect.addEventListener('change', () => {
    updateSelectedState();
    syncInputFromSelection();
  });

  trigger.addEventListener('click', () => {
    if (!trigger.disabled && !isOpen) {
      openListbox();
    }
  });

  trigger.addEventListener('input', () => {
    if (!isOpen) {
      openListbox();
    }
    applyFilter(trigger.value);
    const visible = visibleOptionElements();
    setActiveIndex(visible.length ? 0 : -1);
  });

  trigger.addEventListener('keydown', (e) => {
    if (trigger.disabled) {
      return;
    }
    const visible = visibleOptionElements();
    switch (e.key) {
      case 'ArrowDown':
      case 'Down':
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          openListbox();
        } else if (visible.length) {
          setActiveIndex(activeIndex + 1 >= visible.length ? 0 : activeIndex + 1);
        }
        break;
      case 'ArrowUp':
      case 'Up':
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          openListbox();
        } else if (visible.length) {
          setActiveIndex(activeIndex - 1 < 0 ? visible.length - 1 : activeIndex - 1);
        }
        break;
      case 'Home':
        if (isOpen && visible.length) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen && visible.length) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(visible.length - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (isOpen && activeIndex >= 0 && visible[activeIndex]) {
          selectOption(visible[activeIndex].getAttribute('data-value'));
        } else if (!isOpen) {
          openListbox();
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeListbox({ restoreFocus: true });
        }
        break;
      case 'Tab':
        closeListbox({ restoreFocus: false });
        break;
      default:
        break;
    }
  });

  optionElements.forEach((optionEl) => {
    optionEl.addEventListener('mouseenter', () => {
      setActiveIndex(visibleOptionElements().indexOf(optionEl), { scrollIntoView: false });
    });
    optionEl.addEventListener('click', () => selectOption(optionEl.getAttribute('data-value')));
  });

  const outsideClick = createOutsideClickHandler(container, closeListbox);

  updateSelectedState();
  syncInputFromSelection();

  return {
    container,
    nativeSelect,
    triggerElement: trigger,
    setDisabled(disabled) {
      setTriggerDisabled(container, trigger, disabled);
    },
    destroy() {
      outsideClick.disable();
    },
  };
}

export function createDtpComboboxMultiselect({
  id,
  options = [],
  placeholder = 'Select',
  selectedIds = [],
  labelId,
  onChange,
} = {}) {
  const namespace = id || `dtp-combobox-multiselect-${++instanceCounter}`;

  const container = document.createElement('div');
  container.className = 'dtp-select dtp-multiselect dtp-select--combobox';

  // Hidden native <select multiple> keeps field.inputs[0].value working.
  const nativeSelect = document.createElement('select');
  nativeSelect.className = 'dtp-select__native';
  nativeSelect.multiple = true;
  nativeSelect.id = namespace;
  nativeSelect.tabIndex = -1;
  nativeSelect.setAttribute('aria-hidden', 'true');

  const trigger = createComboboxTrigger({ namespace, labelId, placeholder });
  const chevron = createIcon('chevron', 'dtp-select__chevron');
  const listbox = createListbox({ namespace, labelId, multiselectable: true });

  container.appendChild(createControl(trigger, chevron));
  container.appendChild(listbox);
  container.appendChild(nativeSelect);

  let currentOptions = [];
  let optionElements = [];
  let activeIndex = -1;
  let isOpen = false;
  let selected = new Set(selectedIds || []);

  function syncNativeSelect() {
    nativeSelect.innerHTML = '';
    currentOptions.forEach((option) => {
      const optionEl = document.createElement('option');
      optionEl.value = option.id;
      optionEl.textContent = option.label;
      optionEl.setAttribute('data-valuecode', option.id);
      optionEl.selected = selected.has(option.id);
      nativeSelect.appendChild(optionEl);
    });
  }

  function visibleOptionElements() {
    return optionElements.filter((el) => !el.hidden);
  }

  function updateSelectedState() {
    optionElements.forEach((optionEl, index) => {
      const option = currentOptions[index];
      const isSelected = selected.has(option.id);
      optionEl.setAttribute('aria-selected', String(isSelected));
      optionEl.classList.toggle('dtp-select__option--selected', isSelected);
    });
  }

  // Selected options stay in the list (shown checked) while typing filters it.
  function applyFilter(query) {
    const normalizedQuery = normalize(query);
    optionElements.forEach((optionEl, index) => {
      const option = currentOptions[index];
      const matchesQuery = !normalizedQuery || normalize(option.label).includes(normalizedQuery);
      optionEl.hidden = !matchesQuery;
    });
    listbox.classList.toggle('dtp-select__listbox--empty', !visibleOptionElements().length);
  }

  function setActiveIndex(index, { scrollIntoView = true } = {}) {
    activeIndex = index;
    setActiveIndexShared({
      trigger,
      optionElements: visibleOptionElements(),
      index,
      scrollIntoView,
    });
  }

  function openListbox() {
    if (isOpen || trigger.disabled) {
      return;
    }
    isOpen = true;
    outsideClick.enable();
    container.classList.add('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'true');
    applyFilter(trigger.value);
    const visible = visibleOptionElements();
    setActiveIndex(visible.length ? 0 : -1);
  }

  function closeListbox({ restoreFocus = false } = {}) {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    outsideClick.disable();
    container.classList.remove('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'false');
    setActiveIndex(-1);
    if (restoreFocus) {
      trigger.focus();
    }
  }

  function getSelectedIds() {
    return currentOptions.filter((option) => selected.has(option.id)).map((option) => option.id);
  }

  function selectOption(optionId) {
    if (selected.has(optionId)) {
      selected.delete(optionId);
    } else {
      selected.add(optionId);
    }
    syncNativeSelect();
    updateSelectedState();
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    if (typeof onChange === 'function') {
      onChange(getSelectedIds());
    }
    // Clear input/filter after toggling; the chip renders externally.
    trigger.value = '';
    applyFilter('');

    const visible = visibleOptionElements();
    const keptIndex = visible.findIndex((el) => el.getAttribute('data-value') === optionId);
    setActiveIndex(keptIndex >= 0 ? keptIndex : visible.length ? 0 : -1);
    trigger.focus();
  }

  function renderOptions() {
    listbox.innerHTML = '';
    optionElements = currentOptions.map((option) => {
      const optionEl = createOptionElement(namespace, option, true);
      optionEl.addEventListener('mouseenter', () => {
        setActiveIndex(visibleOptionElements().indexOf(optionEl), { scrollIntoView: false });
      });
      optionEl.addEventListener('click', () => selectOption(option.id));
      listbox.appendChild(optionEl);
      return optionEl;
    });
    applyFilter(trigger.value);
    updateSelectedState();
  }

  function getOptions() {
    return currentOptions.slice();
  }

  function setOptions(newOptions, newSelectedIds = []) {
    currentOptions = newOptions || [];
    selected = new Set(newSelectedIds || []);
    renderOptions();
    syncNativeSelect();
  }

  trigger.addEventListener('click', () => {
    if (!trigger.disabled && !isOpen) {
      openListbox();
    }
  });

  trigger.addEventListener('input', () => {
    if (!isOpen) {
      openListbox();
    }
    applyFilter(trigger.value);
    const visible = visibleOptionElements();
    setActiveIndex(visible.length ? 0 : -1);
  });

  trigger.addEventListener('keydown', (e) => {
    if (trigger.disabled) {
      return;
    }
    const visible = visibleOptionElements();
    switch (e.key) {
      case 'ArrowDown':
      case 'Down':
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          openListbox();
        } else if (visible.length) {
          setActiveIndex(activeIndex + 1 >= visible.length ? 0 : activeIndex + 1);
        }
        break;
      case 'ArrowUp':
      case 'Up':
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          openListbox();
        } else if (visible.length) {
          setActiveIndex(activeIndex - 1 < 0 ? visible.length - 1 : activeIndex - 1);
        }
        break;
      case 'Home':
        if (isOpen && visible.length) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen && visible.length) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(visible.length - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (isOpen && activeIndex >= 0 && visible[activeIndex]) {
          selectOption(visible[activeIndex].getAttribute('data-value'));
        } else if (!isOpen) {
          openListbox();
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeListbox({ restoreFocus: true });
        }
        break;
      case 'Tab':
        closeListbox({ restoreFocus: false });
        break;
      default:
        break;
    }
  });

  const outsideClick = createOutsideClickHandler(container, closeListbox);

  setOptions(options, selectedIds);

  return {
    container,
    nativeSelect,
    triggerElement: trigger,
    setOptions,
    getOptions,
    getSelectedIds,
    setDisabled(disabled) {
      setTriggerDisabled(container, trigger, disabled);
    },
    destroy() {
      outsideClick.disable();
    },
  };
}
